import { Result, Risky } from '@rotomeca/rop';
import { EMPTY_STRING } from '@rotomeca/utils';
import {
	BnumRadioCheckedChangeEvent,
	HTMLBnumFragment,
	HTMLBnumRadio,
	INPUT_BASE_STYLE,
} from 'components/atoms';
import BnumElementInternal from 'components/bnum-element-states';
import { Attr, Autobind, Data, Define, Fire, NO_SETTER } from 'core/decorators';
import { Listen } from 'core/decorators/listen';
import { Schedule } from 'core/decorators/schedule';
import { UI } from 'core/decorators/ui';
import Log from 'core/utils/Log';
import { Nullable, Optional } from '@rotomeca/utils';
import style from './bnum-radio-group.less';
import { NonStd } from 'core/decorators/nonstd';
import { TAG_RADIO_GROUP } from 'core/utils/constants/tags';

//#region Types
/**
 * Interface définissant les éléments UI internes du groupe de boutons radio.
 */
type BnumRadioGroupUi = {
	group: HTMLDivElement;
	legend: HTMLSlotElement;
	hint: HTMLSlotElement;
};

type BnumRadioGroupEvents = {
	[EVENT_CHANGE]: typeof EVENT_CHANGE;
};
//#endregion Types

//#region Global constants
// eslint-disable-next-line quotes
const DEFAULT_LABEL = "Perdu dans l'arbre";
const DEFAULT_HINT = EMPTY_STRING;
const ID_GROUP = 'group';
const ID_LEGEND = 'legend';
const ID_HINT = 'hint';
const DATA_INLINE = 'inline';
const STATE_INLINE = DATA_INLINE;
const ATTR_NAME = 'name';
const EVENT_CHANGE = 'change';
//#endregion Global constants

//#region Template
const TEMPLATE = (
	<HTMLBnumFragment>
		<div class="group__label label-container" id="label">
			<div class="group__label--legend label-container--label">
				<slot id={ID_LEGEND} name="legend">
					{DEFAULT_LABEL}
				</slot>
			</div>
			<div class="group__label--hint label-container--hint">
				<slot id={ID_HINT} name="hint">
					{DEFAULT_HINT}
				</slot>
			</div>
		</div>
		<div
			id={ID_GROUP}
			role="radiogroup"
			class="group__label__group"
			aria-describedby="label"
		>
			<slot></slot>
		</div>
	</HTMLBnumFragment>
);
//#endregion Template

/**
 * Composant `bnum-radio-group`
 *
 * Ce composant représente un groupe de boutons radio. Il gère la sélection unique parmi ses enfants `bnum-radio`,
 * la navigation au clavier, et l'accessibilité (via `role="radiogroup"`).
 *
 * @category Group
 *
 * @structure Structure de base
 * <bnum-radio-group id="groupe1" name="choix" data-label="Faites un choix" data-hint="Indice !">
 *   <bnum-radio value="1">Choix 1</bnum-radio>
 *   <bnum-radio value="2">Choix 2</bnum-radio>
 * </bnum-radio-group>
 *
 * @structure Structure en ligne (inline)
 * <bnum-radio-group id="groupe2" name="inline" data-inline="true" data-label="Choix en ligne">
 *   <bnum-radio value="A">Choix A</bnum-radio>
 *   <bnum-radio value="B">Choix B</bnum-radio>
 * </bnum-radio-group>
 *
 * @slot legend - Description du groupe
 * @slot hint - Indice ou aide pour le groupe
 * @slot (default) - Boutons radio
 *
 * @state inline - Indique si le groupe doit afficher ses options en ligne
 *
 * @attr {string} name - Le nom du groupe de boutons radio. Cet attribut est appliqué à tous les boutons radio enfants pour assurer qu'ils appartiennent au même groupe logique.
 * @attr {string} data-label - Le libellé du groupe.
 * @attr {string} data-hint - L'indice ou l'aide pour le groupe.
 *
 * @event {CustomEvent<{inner:BnumRadioCheckedChangeEvent, caller: HTMLBnumRadioGroup}>} change - Lorsque la valeur change
 *
 * @cssvar {15px} --bnum-radio-group-gap - Espacement entre les boutons radio
 * @cssvar {#666} --bnum-input-hint-text-color - Couleur du texte d'indice ou d'aide
 */
@Define({
	tag: TAG_RADIO_GROUP,
	template: TEMPLATE,
	styles: [INPUT_BASE_STYLE, style],
})
export class HTMLBnumRadioGroup extends BnumElementInternal {
	//#region Private fields
	/**
	 * Instance de MutationObserver pour surveiller les changements dans les enfants du groupe.
	 * @private
	 */
	#_observer: Nullable<MutationObserver> = null;
	//#endregion Private fields

	//#region Getters/Setters
	/**
	 * Références aux éléments du DOM interne.
	 * @private
	 */
	@UI({
		group: `#${ID_GROUP}`,
		legend: `#${ID_LEGEND}`,
		hint: `#${ID_HINT}`,
	})
	accessor #_ui!: BnumRadioGroupUi;

	/**
	 * Le nom du groupe de boutons radio.
	 * Cet attribut est appliqué à tous les boutons radio enfants pour assurer qu'ils appartiennent au même groupe logique.
	 */
	@Attr() accessor name: string = EMPTY_STRING;

	/**
	 * Le libellé du groupe.
	 * @private
	 */
	@Data(NO_SETTER) accessor #_label: string = EMPTY_STRING;

	/**
	 * Le texte d'indice ou d'aide pour le groupe.
	 * @private
	 */
	@Data(NO_SETTER) accessor #_hint: string = EMPTY_STRING;

	/**
	 * La valeur initiale sélectionnée du groupe.
	 * @private
	 */
	@Data(NO_SETTER) accessor #_value: Nullable<string> = null;

	/**
	 * Indique si le groupe doit afficher ses options en ligne (horizontalement).
	 *
	 * @returns {boolean} `true` si le mode inline est activé, sinon `false`.
	 */
	get inline(): boolean {
		return this.data(DATA_INLINE);
	}

	/**
	 * Définit si le groupe doit afficher ses options en ligne.
	 *
	 * @param {boolean} value - La nouvelle valeur pour le mode inline.
	 */
	set inline(value: boolean) {
		this.data(DATA_INLINE, value).#_setInlineState();
	}

	/**
	 * Récupère la liste de tous les éléments `bnum-radio` enfants directs du groupe.
	 *
	 * @returns {HTMLBnumRadio[]} Un tableau contenant les éléments `HTMLBnumRadio`.
	 */
	get radios(): HTMLBnumRadio[] {
		return Array.from(this.querySelectorAll<HTMLBnumRadio>(HTMLBnumRadio.TAG));
	}
	//#endregion Getters/Setters

	//#region Lifecycle
	constructor() {
		super();
	}

	/**
	 * Appelée lorsque le composant est inséré dans le DOM.
	 * Initialise l'observateur de mutations pour détecter l'ajout ou la suppression de boutons radio.
	 */
	connectedCallback(): void {
		super.connectedCallback();
		(this.#_observer ??= new MutationObserver(e => this.#_obserse(e))).observe(
			this,
			{
				childList: true,
			},
		);
	}

	/**
	 * Méthode protégée pour construire le DOM initial.
	 * @protected
	 */
	protected _p_buildDOM(): void {
		this.#_init();
	}

	/**
	 * Méthode protégée appelée lorsqu'un attribut observé change.
	 *
	 * @param {string} name - Le nom de l'attribut modifié.
	 * @param {string | null} oldVal - L'ancienne valeur de l'attribut.
	 * @param {string | null} newVal - La nouvelle valeur de l'attribut.
	 * @returns {void | Nullable<'break'>}
	 * @protected
	 */
	protected _p_update(
		name: string,
		oldVal: string | null,
		newVal: string | null,
	): void | Nullable<'break'> {
		if (name === ATTR_NAME && oldVal !== newVal) {
			this.#_setName();
		}
	}

	/**
	 * Appelée lorsque le composant est retiré du DOM.
	 * Nettoie l'observateur de mutations.
	 * @protected
	 */
	protected _p_detach(): void {
		this.#_observer?.disconnect?.();
	}
	//#endregion Lifecycle

	//#region Private methods
	/**
	 * Initialise le composant.
	 * Configure les écouteurs, les données par défaut, la valeur par défaut et les états.
	 * @private
	 */
	#_init() {
		return this.#_setListeners()
			.#_setDefaultData()
			.#_setDefaultValue()
			.#_initStates();
	}

	/**
	 * Propage le nom du groupe à tous les boutons radio enfants.
	 * @private
	 */
	#_setName() {
		for (const radio of this.radios) {
			radio.name = this.name;
		}

		return this;
	}

	/**
	 * Initialise le libellé (légende) du groupe dans le slot correspondant.
	 * @private
	 */
	#_setLegend() {
		return this.#_initData(this.#_label, this.#_ui.legend);
	}

	/**
	 * Initialise l'indice (hint) du groupe dans le slot correspondant.
	 * @private
	 */
	#_setHint() {
		return this.#_initData(this.#_hint, this.#_ui.hint);
	}

	/**
	 * Helper pour initialiser le contenu textuel d'un slot si des données sont fournies.
	 * @private
	 */
	#_initData(data: Optional<Nullable<string>>, slot: HTMLSlotElement): this {
		if (data) slot.textContent = data;
		return this;
	}

	/**
	 * Initialise les états visuels du composant (ex: inline).
	 * @private
	 */
	#_initStates() {
		return this.#_initInline();
	}

	/**
	 * Applique l'état initial pour le mode inline.
	 * @private
	 */
	#_initInline() {
		if (this.inline) this.#_setInlineState();

		return this;
	}

	/**
	 * Définit les données par défaut (nom, légende, indice).
	 * @private
	 */
	#_setDefaultData() {
		return this.#_setName().#_setLegend().#_setHint();
	}

	/**
	 * Définit la sélection initiale basée sur la valeur par défaut.
	 * Si la valeur par défaut ne correspond à aucun radio, enregistre une erreur ou sélectionne le premier par défaut.
	 * @private
	 */
	#_setDefaultValue() {
		this.#_setDefaultRadioFromValue().match({
			Ok: () => this.#_setDefaultRadio(),
			Err: e => Log.error(this.constructor.name, e.message, e),
		});
		return this;
	}

	/**
	 * Tente de définir le bouton radio sélectionné en fonction de `_value`.
	 * @returns {Result<void>} Résultat de l'opération.
	 * @private
	 */
	@Risky()
	#_setDefaultRadioFromValue(): Result<void> {
		const value = this.#_value;

		if (!value) return Result.Ok();

		const radio = this.radios.find(r => r.value === value);
		if (radio) {
			this.#_check(radio);
			return Result.Ok();
		}

		throw new Error(`Aucun radio trouvé avec la valeur "${value}"`);
	}

	/**
	 * Sélectionne le premier bouton radio par défaut si aucun n'est déjà sélectionné.
	 * @private
	 */
	#_setDefaultRadio(): void {
		if (this.radios.length > 0 && !this.radios.find(x => x.checked)) {
			this.#_check(this.radios[0]);
		}
	}

	/**
	 * Configure les écouteurs d'événements pour le changement de radio et la navigation clavier.
	 * @private
	 */
	#_setListeners(): this {
		this.#_listenRadioChange();
		this.#_handleKeyboardNavigation();
		return this;
	}

	/**
	 * Écoute l'événement de changement d'un bouton radio enfant.
	 * @private
	 */
	@Listen(HTMLBnumRadio.EVENT_CHANGE, { selector: HTMLBnumRadio.TAG })
	#_listenRadioChange() {
		return this.#_handleRadioChange;
	}

	/**
	 * Gère la navigation au clavier (flèches directionnelles) entre les boutons radio.
	 * @private
	 */
	@Listen('keydown')
	#_handleKeyboardNavigation() {
		return (e: KeyboardEvent) => {
			const isRTL = getComputedStyle(this).direction === 'rtl';
			const KEY_NEXT = ['ArrowDown', isRTL ? 'ArrowLeft' : 'ArrowRight'];
			const KEY_PREV = ['ArrowUp', isRTL ? 'ArrowRight' : 'ArrowLeft'];
			const ALL_KEYS = [...KEY_NEXT, ...KEY_PREV];

			if (!ALL_KEYS.includes(e.key)) return;

			e.preventDefault();

			const radios = this.radios;
			if (radios.length === 0) return;

			const currentRadio = radios.find(r => r.checked);
			const currentIndex = currentRadio ? radios.indexOf(currentRadio) : -1;
			const direction = KEY_PREV.includes(e.key) ? -1 : 1;

			const nextIndex =
				(currentIndex + direction + radios.length) % radios.length;

			const targetRadio = radios[nextIndex];

			if (targetRadio) {
				this.#_check(targetRadio, { fire: true });
				targetRadio.internalCheckbox.focus();
			}
		};
	}

	/**
	 * Gère le changement d'état d'un bouton radio.
	 * Assure qu'un seul radio est sélectionné à la fois.
	 *
	 * @param {CustomEvent} e - L'événement de changement.
	 * @private
	 */
	@Autobind
	#_handleRadioChange(
		e: CustomEvent<{ innerEvent: BnumRadioCheckedChangeEvent }>,
	) {
		e.stopPropagation();
		const { innerEvent: inner } = e.detail;

		if (inner.checked) this.#_check(inner.caller).#_fireChange(inner);
	}

	/**
	 * Notifie les écouteurs externes qu'un changement de sélection a eu lieu.
	 *
	 * @param {BnumRadioCheckedChangeEvent} e - L'événement interne du radio.
	 * @returns L'événement formaté.
	 * @private
	 */
	@Fire('change')
	#_fireChange(e: BnumRadioCheckedChangeEvent) {
		return { inner: e, caller: this };
	}

	/**
	 * Marque un bouton radio comme sélectionné et désélectionne les autres.
	 * Met à jour les attributs `tabindex` pour la navigation clavier.
	 *
	 * @param {HTMLBnumRadio} radio - Le bouton radio à sélectionner.
	 * @param {object} options - Options supplémentaires.
	 * @param {boolean} [options.fire=false] - Si `true`, déclenche l'événement de changement sur le radio.
	 * @private
	 */
	#_check(
		radio: HTMLBnumRadio,
		{ fire = false }: { fire?: boolean } = {},
	): this {
		for (const r of this.radios) {
			if (r !== radio) {
				r.checked = false;
				this.#_stopFocus(r);
			}
		}

		if (fire) radio.updateCheckAndFire(true);
		else radio.checked = true;

		this.#_canFocus(radio);

		return this;
	}

	/**
	 * Fonction de rappel pour l'observateur de mutations.
	 * Détecte si de nouveaux boutons radio sont ajoutés et planifie leur initialisation.
	 * @private
	 */
	#_obserse(mutations: MutationRecord[]) {
		const hasOptionMutation = mutations.some(m =>
			Array.from(m.addedNodes).some(n => n instanceof HTMLBnumRadio),
		);

		if (hasOptionMutation) {
			this.#_scheduleSetupRadios();
		}
	}

	/**
	 * Planifie la configuration des radios (utilisé lors de mutations dynamiques).
	 * @private
	 */
	@Schedule()
	#_scheduleSetupRadios() {
		this.#_setupRadios();
	}

	/**
	 * Configure l'état initial des boutons radio (nom, sélection, focus).
	 * @private
	 */
	#_setupRadios() {
		let hasChecked = false;
		for (const radio of this.radios) {
			if (radio.name !== this.name) radio.name = this.name;
			if (radio.checked) {
				if (!hasChecked) {
					hasChecked = true;
					this.#_canFocus(radio);
				} else {
					radio.checked = false;
					this.#_stopFocus(radio);
				}
			} else this.#_stopFocus(radio);
		}

		return this;
	}

	/**
	 * Autorise le focus sur le radio.
	 * @param radio Bouton radio concerné.
	 * @returns Chaîne
	 */
	#_canFocus(radio: Readonly<HTMLBnumRadio>) {
		radio.setAttribute('tabindex', '0');
		return this;
	}

	/**
	 * Retire le focus du radio.
	 * @param radio Bouton radio concerné.
	 * @returns Chaîne
	 */
	#_stopFocus(radio: Readonly<HTMLBnumRadio>) {
		radio.setAttribute('tabindex', '-1');
		return this;
	}

	/**
	 * Applique ou retire l'état visuel "inline" sur le composant.
	 * @private
	 */
	#_setInlineState() {
		if (this.inline) this._p_addState(STATE_INLINE);
		else this._p_removeState(STATE_INLINE);
	}
	//#endregion Private methods

	//#region Static
	/**
	 * Liste des attributs observés par le composant.
	 * @returns {string[]} Liste des noms d'attributs.
	 * @protected
	 * @static
	 * @override
	 * @deprecated Utilisez le décorateur {@link Observe} du commit 3e38db0162eef596874dbe32490d9e96b09fb1c0
	 * @see [feat(composants): ✨ Ajout d'un décorateur pour réduire le boilerplate des attibuts à observer](https://github.com/messagerie-melanie2/design-system-bnum/commit/3e38db0162eef596874dbe32490d9e96b09fb1c0)
	 */
	@NonStd('Deprecated')
	protected static _p_observedAttributes(): string[] {
		return [ATTR_NAME];
	}

	/**
	 * Evènements disponibles pour ce composant.
	 * @readonly
	 */
	static get EVENTS(): Readonly<BnumRadioGroupEvents> {
		return {
			[EVENT_CHANGE]: EVENT_CHANGE,
		};
	}
	//#endregion Static
}
